//_components/onboarding/registration/AccountRegisterStep3.tsx

"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { registerSchema, RegisterFormData } from "@/lib/schemas/authSchema";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import PasswordInput from "@/components/ui/password-input";

type Step3Props = {
  onBack: () => void;
  onSubmit: (data: RegisterFormData) => void;
  defaultValues?: Partial<RegisterFormData>;
  isSubmitting?: boolean;
};

export default function AccountRegisterStep3({
  onBack,
  onSubmit,
  defaultValues,
  isSubmitting = false,
}: Step3Props) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues,
  });

  return (
    <form className="text-center" onSubmit={handleSubmit(onSubmit)}>
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-semibold text-gray-800"
      >
        Account Information
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-2 text-sm text-gray-600"
      >
        Enter your details to create your account and start exploring or
        offering solar solutions.
      </motion.p>

      <div className="mt-8 space-y-5 text-left">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <input
            type="text"
            {...register("name")}
            placeholder="John Smith"
            className={`w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 ${
              errors.name ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            placeholder="you@email.com"
            {...register("email")}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${
              errors.email ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone
          </label>
          <input
            type="tel"
            placeholder="+234 | 8063223670"
            {...register("phone")}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${
              errors.phone ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.phone && (
            <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <PasswordInput
            control={control as any}
            label="Password"
            {...register("password")}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${
              errors.password ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.password && (
            <p className="mt-1 text-xs text-red-500">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <PasswordInput
            control={control as any}
            // name="confirmPassword"
            label="Confirm Password"
            showStrengthMeter={false}
            {...register("confirmPassword")}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${
              errors.confirmPassword ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-xs text-red-500">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>
      </div>

      <div className="mt-10 flex justify-between flex-col md:flex-row gap-4">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-medium hover:bg-gray-100"
        >
          ← Back
        </button>
        <motion.button
          type="submit"
          whileTap={{ scale: 0.95 }}
          disabled={isSubmitting}
          className={`px-6 py-3 rounded-full font-medium ${
            isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-800 text-white hover:bg-green-700"
          }`}
        >
          {isSubmitting ? "Creating..." : "Create Account"}
        </motion.button>
      </div>
    </form>
  );
}
